import { MatSnackBar } from '@angular/material/snack-bar';
import * as THREE from '@phdsoft/three';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { INSPECTIONS_TYPES_IDS } from 'projects/phdsoft/common/src/lib/shared/constants/inspections-types-ids';
import { Asset } from '../platform/busines-data/Framework/Asset';
import { Collection } from '../platform/busines-data/Campaigns/Collection';
import type { Section } from '../platform/busines-data/Framework/Section';
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { StructuralElement } from '../platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { WorkableElement } from '../platform/busines-data/SectionElements/WorkableElement';
import { GraphicsMaterials } from '../digital-twin/graphics/graphics-materials';
import { Graphics } from '../digital-twin/graphics/graphics';
import { GraphicsRenderDecal } from '../digital-twin/graphics/graphics-render-decal';
import type { InspectionPointInstanceManager } from '../digital-twin/graphics/inspection-point-instance-manager.service';
import { CollectionService } from '../collections/collection.service';
import { InspectionEventsService } from '@app/template/view-mode-tools/view-mode-anomaly-labels/inspection-events.service';
import { AssetManager } from './asset-manager';
import {
  buildRetargetedResult, checklistDaysWithExecution, crossRegionBlockReason, distinctChecklistDays, followupDateFor, pointShapeForScope, pointTypeForTarget,
  retargetMethodOf, RetargetValidateOptions, scopeForTarget, validateRetarget, worldNormalFromIntersection,
} from './inspection-retarget.util';
import type { RetargetRegionPort } from './inspection-retarget-region-port.service';
import { RetargetRegionSwitch } from './inspection-retarget-region-switch';
import { RetargetOriginGhost, RetargetPointPreview, RetargetTargetHighlight } from './inspection-retarget-preview';
import { elementRefFromHit, guidVariants, sameGuid } from './inspection-retarget-hit.util';
import { retargetInstancedPoint, RetargetSnapshot, rollbackRetarget, takeRetargetSnapshot } from './inspection-retarget-transaction';

type MaterialSlot = THREE.Material | THREE.Material[];
/** `color`: exact instance colour of an instanced point (its material setter maps colours by name only). */
interface SavedMaterial { el: WorkableElement; mesh: THREE.Mesh; saved: MaterialSlot; applied: THREE.Material; color?: THREE.Color; }

/** Hit on an inspection: an instanced POINT or a real mesh (AREA/ELEMENT/orphan point). */
interface InspectionHit { el: GaugingElement; distance: number; }

/**
 * "Change inspection target" tool (functionalityMode INSPECSELECTCHANGE -> INSPECCHANGETARGET),
 * in SECTIONS and REGIONS. Owns its own phase-specific raycast, so inspections become pickable
 * in REGIONS only while this tool is armed (Asset.GetVisibleGroups is not touched).
 * Instantiated by AssetManagerInspection like AssetManagerInspectionDrag.
 *
 * Cross-region (POINT only): the dialog hands over a RetargetRegionPort (`setRegionPort`) and calls
 * `chooseRegion(id)`, which shows that region; a target in it is then accepted. Cancel/close and a
 * successful Apply return the view to where the user started (`returnToOrigin`); `cancel()` alone
 * (tool ended by someone else) never navigates. There is no destroy hook: `cancel()` calls
 * `regionSwitch.forget()`, which bumps the switch generation, so a pending readiness poll becomes
 * inert (its next timer tick resolves `chooseRegion` false without touching the view).
 */
export class AssetManagerInspectionRetarget {
  private inspection: GaugingElement | null = null;
  private target: WorkableElement | null = null;
  private lastRayDirection: THREE.Vector3 | null = null;
  private targetNormal: THREE.Vector3 | null = null;
  private savedMaterials = new Map<string, SavedMaterial>();
  private readonly preview = new RetargetPointPreview(() => this.graphics?.scene);
  /** Marks the picked inspection's original spot while its region is hidden by a tool region switch. */
  private readonly originGhost = new RetargetOriginGhost(() => this.graphics?.scene, () => this.pointManager);
  /** Green target highlight while the target's own mesh is hidden (new-GLTF REGIONS: merged lightsection shown). */
  private readonly targetHighlight = new RetargetTargetHighlight(() => this.graphics?.scene);
  private busy = false;
  /** Set and cleared by the Change Inspection Target dialog; null when the tool runs without it. */
  private regionPort: RetargetRegionPort | null = null;
  public readonly regionSwitch: RetargetRegionSwitch;

  private graphics: Graphics;
  private graphicsRenderDecal: GraphicsRenderDecal;
  private assetManager: AssetManager;
  private collectionService: CollectionService;
  private snackBar: MatSnackBar;
  private inspectionEventsService: InspectionEventsService | null;
  /** Draws the targeted POINT inspections (dev GPU instancing); null until one exists. */
  private instanceManager: InspectionPointInstanceManager | null = null;
  private prepareInspectionBeforeSaving: (element: GaugingElement) => Promise<ElementResultInfo>;

  public get pickedInspection(): GaugingElement | null { return this.inspection; }
  public get pickedTarget(): WorkableElement | null { return this.target; }
  public get isBusy(): boolean { return this.busy; }
  public get isSwitchingRegion(): boolean { return this.regionSwitch.switching; }
  public get chosenRegionId(): string | null { return this.regionSwitch.chosenRegionId; }

  /** `regionSwitch` wait/poll in ms (tests only; production uses the RetargetRegionSwitch defaults). */
  constructor(regionSwitchTiming?: { waitMs?: number; pollMs?: number }) {
    this.regionSwitch = new RetargetRegionSwitch(() => this.regionPort, regionSwitchTiming?.waitMs, regionSwitchTiming?.pollMs);
  }

  public setRegionPort(port: RetargetRegionPort | null): void {
    this.regionPort = port ?? null;
  }

  public initialize(
    graphics: Graphics,
    graphicsRenderDecal: GraphicsRenderDecal,
    assetManager: AssetManager,
    collectionService: CollectionService,
    snackBar: MatSnackBar,
    prepareInspectionBeforeSaving: (element: GaugingElement) => Promise<ElementResultInfo>,
    inspectionEventsService?: InspectionEventsService | null,
    instanceManager?: InspectionPointInstanceManager | null,
  ): void {
    this.graphics = graphics;
    this.graphicsRenderDecal = graphicsRenderDecal;
    this.assetManager = assetManager;
    this.collectionService = collectionService;
    this.snackBar = snackBar;
    this.prepareInspectionBeforeSaving = prepareInspectionBeforeSaving;
    this.inspectionEventsService = inspectionEventsService ?? null;
    this.instanceManager = instanceManager ?? GaugingElement.instanceManager ?? null;
  }

  public isRetargetMode(): boolean {
    const mode = this.assetManager?.functionalityMode;
    return mode === 'INSPECSELECTCHANGE' || mode === 'INSPECCHANGETARGET';
  }

  /** Click while the tool is armed: raycasts inspections (pick phase 1) or structure (phase 2). */
  public handleClick(ev: any, asset: Asset, camera: THREE.Camera, offsetX: number, offsetY: number): void {
    if (!ev || !asset || !camera || this.busy || this.regionSwitch.switching || !this.isRetargetMode()) return;
    // NDC exactly like DTSelectionService.identifyIntersection
    const src = ev.changedTouches ? ev.changedTouches[0] : ev;
    const x = src.clientX ?? ev.center?.x;
    const y = src.clientY ?? ev.center?.y;
    if (x == null || y == null) return;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(((x - offsetX) / window.innerWidth) * 2 - 1, -((y - offsetY) / window.innerHeight) * 2 + 1), camera);

    if (this.assetManager.functionalityMode === 'INSPECSELECTCHANGE') {
      // instanced POINTs live in the structure sections' batches, the others in the campaign groups
      const instanced = this.instancedInspectionHit(raycaster);
      const groups = (asset.InspectionCampaigns ?? []).filter(c => c.group.visible).flatMap(c => c.VisibleRegionsGroups);
      const hit = this.firstVisibleMeshHit(raycaster.intersectObjects(groups, true));
      const el = hit ? this.findElement(asset.InspectionCampaigns, hit) : null;
      const real: InspectionHit | null = el instanceof GaugingElement ? { el, distance: hit.distance } : null;
      const nearest = instanced && (!real || instanced.distance < real.distance) ? instanced : real;
      if (nearest) this.pickInspection(nearest.el);
      return;
    }
    // Targets only from StructureCampaigns (never lightCampaign: its elements fail validateRetarget).
    // firstVisibleMeshHit skips the instanced inspection batches parented to the structure sections.
    // New-GLTF REGIONS: the hit is on a merged lightsection mesh (the detailed meshes are hidden);
    // findElement resolves it through the mesh's element index, and hit.point places the POINT preview.
    const hit = this.firstVisibleMeshHit(raycaster.intersectObjects(asset.GetStructureVisibleGroups(), true));
    if (!hit) return;
    const el = this.findElement(asset.StructureCampaigns, hit);
    if (!el) {
      this.notify('Select a structural element as the new target.');
      return;
    }
    this.lastRayDirection = raycaster.ray.direction.clone();
    this.pickTarget(el, hit);
  }

  public pickInspection(el: GaugingElement): void {
    if (!el?.elementResult || this.busy || el === this.inspection) return;
    if ((this.assetManager?.tempInsertedPointsArray ?? []).some(p => p === el || p?.Guid === el.Guid)) {
      this.notify('Save the inserted point before changing its target.');
      return;
    }
    this.clearTarget();
    if (this.inspection) this.restoreMaterial(this.inspection);
    this.inspection = el;
    this.paintInspection(el);
    this.syncOriginGhost(); // a new pick while another region is shown: the ghost follows it
  }

  public pickTarget(el: WorkableElement, hit: THREE.Intersection): void {
    if (this.busy) return;
    if (!this.inspection) {
      this.notify('Pick the inspection to change first.');
      return;
    }
    const validation = validateRetarget(this.inspection, el, this.validateOptions());
    if (validation.ok === false) {
      this.notify(validation.reason);
      return;
    }
    // gate on the region, not on el.properties: a target without properties in a loaded region is fine
    if (this.isRegionLoading(el.regionId)) {
      this.notify('Properties of this region are still loading.');
      return;
    }
    const normal = this.normalFor(el, hit);
    let built: THREE.Mesh | null = null;
    if (retargetMethodOf(this.inspection.elementResult.InspectionMethodId) === 'POINT') {
      built = this.buildPointMesh(el, hit?.point, normal);
      if (!built) {
        this.notify('An inspection point cannot be placed on the selected element.');
        return;
      }
    }
    this.clearTarget(); // puts a previous preview back first, so the saved pose is the original
    this.target = el;
    this.targetNormal = normal;
    this.paint(el, GraphicsMaterials.greenMaterial);
    // the paint only shows on a visible mesh (SECTIONS, old format); a hidden one gets the overlay
    if (this.isRealMesh(el) && !this.isVisibleChain(el.SurfaceDesign)) this.targetHighlight.show(el.SurfaceDesign);
    if (built) {
      built.userData.retargetPreview = true; // never a target (firstVisibleMeshHit)
      // overlay when the real mesh is hidden (its region was hidden by a cross-region switch); an
      // instanced point is always an overlay and its instance is hidden while the preview shows
      this.preview.show(this.inspection, built, !this.isInspectionShown(this.inspection));
    }
    this.requestRender();
  }

  /**
   * Shows `regionId` so a target can be picked in it (dialog region dropdown). Resolves true once the
   * region's properties are loaded; false when refused, still loading, or aborted by `cancel()`.
   */
  public async chooseRegion(regionId: string): Promise<boolean> {
    const inspection = this.inspection;
    if (!inspection || this.busy || !regionId) return false;
    if (!this.regionPort) {
      this.notify('Open the Change Inspection Target window to choose a region.');
      return false;
    }
    const blockReason = (inspection.regionIds ?? []).includes(regionId) ? null : crossRegionBlockReason(inspection);
    if (blockReason) {
      this.notify(blockReason);
      return false;
    }
    this.clearTarget();
    const ok = await this.regionSwitch.choose(regionId);
    this.syncOriginGhost(); // the region is shown even when its properties are still loading
    if (!ok) {
      // chosenRegionId is null when cancel()/forget() aborted the wait: nothing to report
      if (this.regionSwitch.chosenRegionId === regionId) this.notify('Region is still loading. Try again in a moment.');
      return false;
    }
    if (this.inspection !== inspection) return false; // tool cancelled meanwhile
    this.assetManager.functionalityMode = 'INSPECCHANGETARGET';
    // showing the region may repaint the inspection (updateViewMode): paint it again
    this.restoreMaterial(inspection);
    this.paintInspection(inspection);
    return true;
  }

  /**
   * Dialog Cancel/close: clears the target pick and returns the view to where the tool started
   * (true when it navigated). The inspection stays picked; `cancel()` still has to be called.
   */
  public returnToOrigin(): boolean {
    this.clearTarget();
    const navigated = this.regionSwitch.returnToOrigin();
    this.syncOriginGhost();
    if (this.inspection) {
      this.restoreMaterial(this.inspection);
      this.paintInspection(this.inspection);
    }
    return navigated;
  }

  /**
   * Undo/redo while the tool is armed and shows a region it chose: returns to the origin view (not
   * recorded in undo/redo) and ends the tool through the functionalityMode setter, which calls
   * `cancel()`. True when it did so (the key press is consumed); false leaves everything untouched.
   */
  public returnToOriginAndEnd(): boolean {
    if (!this.isRetargetMode() || !this.regionSwitch.isShowingToolRegion()) return false;
    this.returnToOrigin();
    this.assetManager.setFunctionalityModeNone();
    return true;
  }

  /** Applies the retarget locally, saves it, and rolls everything back if the save fails. */
  public async execute(): Promise<boolean> {
    // captured now: the dialog clears the port on close, which may happen while the save is pending
    const port = this.regionPort;
    const inspection = this.inspection;
    const target = this.target;
    const normal = this.targetNormal;
    if (this.busy) return false;
    if (!inspection || !target) {
      this.notify('Pick an inspection and its new target first.');
      return false;
    }
    const validation = validateRetarget(inspection, target, this.validateOptions());
    if (validation.ok === false) {
      this.notify(validation.reason);
      return false;
    }
    const method = retargetMethodOf(inspection.elementResult.InspectionMethodId);
    // an instanced POINT keeps its phantom: Apply moves its instance slot, never assigns a geometry
    const realMesh = this.isRealMesh(inspection);
    let pointMesh: THREE.Mesh | null = null;
    if (method === 'POINT') {
      // the previewed point becomes the new pose; its original pose is put back first so the
      // snapshot below records the original (execute() owns the built shape from here on)
      pointMesh = this.preview.inspection === inspection ? this.preview.take() : null;
      if (!pointMesh) {
        this.notify('An inspection point cannot be placed on the selected element.');
        return false;
      }
    }

    this.busy = true;
    const oldTarget = inspection.targetElement;
    // POINT on its current target: a reposition (validateRetarget rejects it for other methods); snackbar wording only
    const reposition = (oldTarget?.Guid ?? inspection.elementResult.TargetGeometryId) === target.Guid;
    let snapshot: RetargetSnapshot | null = null;
    let dto: ElementResultInfo | null = null;
    let typeName: string | undefined;
    try {
      try {
        snapshot = takeRetargetSnapshot(inspection, target, this.pointManager);
        const newResult = buildRetargetedResult(inspection.elementResult, target, pointMesh
          ? { point: pointMesh.position.clone(), normal } : {});
        if (!newResult.RegionIds?.length) newResult.RegionIds = [target.regionId];
        this.applyLocally(inspection, target, newResult, method, pointMesh, snapshot);
        dto = await this.prepareInspectionBeforeSaving(inspection);
        if (!dto.RegionIds?.length) dto.RegionIds = [target.regionId]; // backend validator requires it
        typeName = await this.resolveTypeName(port, dto); // once: the save and the new-region plan share it
        // never null: a null follow-up date hangs saveInspectionElement
        const followupDate = followupDateFor(inspection.checkList, this.assetManager.referenceDate ?? new Date());
        await this.collectionService.saveInspectionElement([dto], followupDate, typeName);
      } catch (error) {
        console.error('[InspectionRetarget] save failed, rolling back', error);
        const tried = realMesh ? inspection.SurfaceDesign.geometry as THREE.BufferGeometry : undefined;
        if (snapshot) rollbackRetarget(inspection, target, snapshot, this.pointManager);
        // release the shapes of this attempt once the mesh no longer shows them (each exactly once);
        // a phantom has no geometry: only the built point's shape is released
        for (const g of new Set([tried, pointMesh?.geometry as THREE.BufferGeometry])) {
          if (g && g !== inspection.SurfaceDesign.geometry) g.dispose();
        }
        this.finishPicks(inspection);
        this.notify('Could not change the inspection target. The change was reverted.');
        return false;
      }

      // saved: the point lives in the chosen region now; drop the ghost before its shared shape is released
      this.originGhost.remove();
      if (realMesh) {
        if (snapshot.geometry !== inspection.SurfaceDesign.geometry) snapshot.geometry?.dispose();
      } else {
        pointMesh?.geometry.dispose(); // only its pose was taken; the batch draws the cached shape
      }
      inspection.saved = true;
      await this.reconcileNewRegionPlan(port, inspection, snapshot, dto, typeName); // first: never depends on the old one
      await this.reconcileOldRegionPlan(port, inspection, snapshot, dto);
      this.invalidateTimelinePlans(inspection, snapshot);
      this.regionSwitch.returnToOrigin(); // user decision: back to the starting view after Apply
      this.restoreMaterial(inspection);
      this.refreshColorsAndDegradation(inspection, oldTarget, snapshot);
      this.inspectionEventsService?.emitSaveEvent([inspection]);
      this.patchCollectionState(inspection, snapshot.regionId);
      this.assetManager.currentSelectedElement = inspection;
      this.finishPicks(inspection);
      this.requestRender(); // the moved instance slot is uploaded on the next frame
      const movedRegion = snapshot.regionId !== inspection.regionId;
      this.notify(reposition ? 'Inspection point moved.' : movedRegion
        ? `Inspection target changed. Moved to region ${inspection.regionName || inspection.regionId}.`
        : 'Inspection target changed.');
      return true;
    } finally {
      this.busy = false;
    }
  }

  /** Restores the saved materials, removes the preview and forgets both picks. */
  public cancel(): void {
    for (const entry of this.savedMaterials.values()) this.restoreEntry(entry);
    this.savedMaterials.clear();
    this.preview.remove();
    this.targetHighlight.remove();
    this.originGhost.remove();
    this.regionSwitch.forget(); // never navigates; the dialog calls returnToOrigin() first when it should
    this.inspection = null;
    this.target = null;
    this.lastRayDirection = null;
    this.targetNormal = null;
    this.requestRender();
  }

  //#region execute() steps

  private applyLocally(inspection: GaugingElement, target: WorkableElement, newResult: ElementResultInfo,
    method: string, pointMesh: THREE.Mesh | null, snapshot: RetargetSnapshot): void {
    const mesh = inspection.SurfaceDesign;
    const realMesh = this.isRealMesh(inspection);
    if (pointMesh) {
      // keep the SurfaceDesign instance (outline/label references), take the new pose and shape;
      // an instanced point takes the pose in retargetInstancedPoint below
      if (realMesh) {
        mesh.position.copy(pointMesh.position);
        mesh.rotation.copy(pointMesh.rotation);
        mesh.scale.copy(pointMesh.scale);
        mesh.geometry = pointMesh.geometry;
      }
    } else if (method === 'ELEMENT' && target.SurfaceDesign?.geometry) {
      mesh.geometry = target.SurfaceDesign.geometry.clone();
    } // AREA / other: ids and scope only, geometry untouched

    const oldTarget = snapshot.targetElement;
    if (snapshot.oldListIndex >= 0) (oldTarget as StructuralElement).gaugingElements.splice(snapshot.oldListIndex, 1);
    const listed = snapshot.oldListIndex >= 0 || (!oldTarget && method === 'POINT');
    if (listed && target instanceof StructuralElement && !target.gaugingElements.includes(inspection)) {
      snapshot.addedToNewList = true; // before the call: addGaugingElement pushes, then may throw
      target.addGaugingElement(inspection);
    }

    const moves = snapshot.SectionId !== target.SectionId || snapshot.regionId !== target.regionId;
    const campaign = snapshot.campaign ?? this.assetManager.asset?.InspectionCampaigns?.[0];
    if (!realMesh) {
      // pose, campaign listing, structure section and instance slot (batch of the new scope's shape)
      retargetInstancedPoint(inspection, target, {
        pointMesh, shapeType: pointShapeForScope(newResult.Scope), moves, campaign, manager: this.pointManager,
      }, snapshot);
    } else {
      if (moves) snapshot.section?.detachElementByIdOnly(inspection.Guid);
      inspection.retarget(target);
      if (moves) campaign?.addSurfaceElement(inspection); // Collection -> Region -> Section (created if missing)
    }
    inspection.scope = newResult.Scope;
    inspection.elementResult = newResult; // setter re-links Element
    // an instance follows its section group: an explicit hide would outlive the section being shown again
    if (realMesh) mesh.visible = this.isSectionVisible(target);
  }

  /** Mirrors AssetManagerInspectionDrag.handleDegradationOrColorUpdateForElement, for the old target too. */
  private refreshColorsAndDegradation(inspection: GaugingElement, oldTarget: WorkableElement | null, s: RetargetSnapshot): void {
    const am = this.assetManager;
    try {
      if (am.degradationMode && inspection.elementResult.InspectionTypeId === INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING) {
        if (am.shouldEstimateDegradationCurve(inspection)) {
          am.estimateElementDegradationCurve(inspection.Guid, inspection.targetElementId, false);
        } else {
          am.removeInspectionDegradationCurveParameters(inspection.Guid);
          am.estimateDegradationCurvesByTarget(inspection.targetElementId, inspection.regionIds, inspection.SectionId);
        }
        if (oldTarget) am.estimateDegradationCurvesByTarget(oldTarget.Guid, s.regionIds, s.SectionId);
      } else {
        am.updateInspectionColorById(inspection.Guid);
      }
      am.deletePointFromTempArray?.(inspection);
    } catch (error) {
      console.error('[InspectionRetarget] colour/degradation refresh failed', error);
    }
  }

  /** InsertedRegionId before and after the save; the plan reconciles run only when they differ. */
  private planRegions(inspection: GaugingElement, s: RetargetSnapshot): { oldRegion: string; newRegion: string } {
    return { oldRegion: s.elementResult.InsertedRegionId ?? s.regionId, newRegion: inspection.elementResult.InsertedRegionId };
  }

  /**
   * Inspection type name for the plan calls (the backend stores it as the plan name): through the port
   * (which also fetches the types when view mode never loaded them), else the locally loaded list.
   * Never throws: an unresolved name must not block the save; it is only warned about.
   */
  private async resolveTypeName(port: RetargetRegionPort | null, dto: ElementResultInfo): Promise<string | undefined> {
    const typeId = dto.InspectionTypeId;
    let name: string | undefined;
    try {
      name = await port?.inspectionTypeName(typeId, dto.InspectionTypeAlias);
    } catch (error) {
      console.warn('[InspectionRetarget] inspection type name lookup failed', error);
    }
    name ??= this.assetManager.listInpectionType?.find(t => t.id === typeId)?.name;
    if (name === undefined) console.warn('[InspectionRetarget] inspection type name unresolved', typeId);
    return name;
  }

  /**
   * Cross-region move saved: the new region needs a plan (or cluster) for every checklist day, the
   * planned one and the clustered ones. The save's own plan call is region-agnostic (ClusterState)
   * and covers the follow-up date only, so it is reconciled here per day (online only). Never throws:
   * a failure only leaves the new region without its plan. `port` as for reconcileOldRegionPlan.
   */
  private async reconcileNewRegionPlan(port: RetargetRegionPort | null, inspection: GaugingElement,
    s: RetargetSnapshot, dto: ElementResultInfo, typeName: string | undefined): Promise<void> {
    try {
      const { oldRegion, newRegion } = this.planRegions(inspection, s);
      if (!port || !newRegion || oldRegion === newRegion || !navigator.onLine) return;
      await port.reconcileNewRegionPlan(
        dto.AssetId ?? this.assetManager.asset?.assetInfo?.AssetId, newRegion, dto.InspectionTypeId, typeName,
        checklistDaysWithExecution(dto.CheckList ?? []));
    } catch (error) {
      console.warn('[InspectionRetarget] new-region plan not reconciled', error);
    }
  }

  /**
   * Cross-region move saved: the old region's inspection plan loses this inspection, so it is
   * recomputed per checklist day (online only), after the new region's. Never throws: a failure only
   * leaves counters stale. `port` is the one execute() started with (still valid after the dialog
   * closed during the save).
   */
  private async reconcileOldRegionPlan(port: RetargetRegionPort | null, inspection: GaugingElement,
    s: RetargetSnapshot, dto: ElementResultInfo): Promise<void> {
    try {
      const oldRegion = s.elementResult.InsertedRegionId ?? s.regionId;
      if (!port || !oldRegion || oldRegion === inspection.elementResult.InsertedRegionId || !navigator.onLine) return;
      const checkList: any[] = dto.CheckList ?? [];
      await port.reconcileOldRegionPlan(
        dto.AssetId ?? this.assetManager.asset?.assetInfo?.AssetId, oldRegion, dto.InspectionTypeId,
        distinctChecklistDays(checkList), checkList.some(c => !!c && c.Value !== ''));
    } catch (error) {
      console.warn('[InspectionRetarget] old-region plan not reconciled', error);
    }
  }

  /**
   * After the plan reconciles, before returnToOrigin(): loadPlansToTimeline/loadHistoricalPlans only
   * refetch when their cache is unset or holds another region's plans, and `[]` (a region with no
   * plans) never does, so the cache is dropped for the next region-ids emission to refetch (online only).
   */
  private invalidateTimelinePlans(inspection: GaugingElement, s: RetargetSnapshot): void {
    const { oldRegion, newRegion } = this.planRegions(inspection, s);
    if (oldRegion === newRegion || !navigator.onLine) return;
    this.assetManager.plans = null;
    this.assetManager.historicalPlans = null;
  }

  /**
   * Collection-state ElementsResult records: after a region change the record is moved to the new
   * region's blob (CollectionState.removeElement looks there); otherwise patched in place.
   */
  private patchCollectionState(inspection: GaugingElement, oldRegionId: string): void {
    try {
      const info = this.collectionService.getById(inspection.collectionId);
      if (!info) return;
      if (oldRegionId !== inspection.regionId) this.moveStateRecords(info, inspection);
      const records = [
        ...(info.ElementsResult ?? []),
        ...(info.BlobPaths ?? []).flatMap(b => (b.ResultBlob ?? []).flatMap(r => r.ElementsResult ?? [])),
      ];
      for (const record of records.filter(r => r?.id === inspection.Guid)) {
        record.TargetGeometryId = inspection.targetElementId;
        record.TargetGeometryTypeId = inspection.targetElementType;
        record.SectionId = inspection.SectionId;
        record.InsertedRegionId = inspection.elementResult.InsertedRegionId;
        record.Scope = inspection.scope;
      }
      if (info.SectionsIds && !info.SectionsIds.includes(inspection.SectionId)) info.SectionsIds.push(inspection.SectionId);
    } catch (error) {
      console.warn('[InspectionRetarget] collection state not patched', error);
    }
  }

  /** Adds a record in the inspection's region blob first, then drops the ones left in other region blobs. */
  private moveStateRecords(info: ReturnType<CollectionService['getById']>, inspection: GaugingElement): void {
    if (info.IsTemplate) return; // template collections keep records in info.ElementsResult, not per region
    const stale = (info.BlobPaths ?? []).filter(b => b.RegionId !== inspection.regionId)
      .flatMap(b => (b.ResultBlob ?? []).filter(r => (r.ElementsResult ?? []).some(e => e?.id === inspection.Guid)));
    if (!stale.length) return;
    try {
      // addElement first: if it throws, the old records stay and the caller patches them in place
      this.collectionService.addElement(inspection, inspection.Campaign?.Date ?? this.assetManager.referenceDate, false);
    } catch (error) {
      console.warn('[InspectionRetarget] collection-state record not moved', error);
      return;
    }
    for (const r of stale) r.ElementsResult = r.ElementsResult.filter(e => e?.id !== inspection.Guid);
  }

  //#endregion

  //#region helpers

  /**
   * After execute: target pick and preview cleared, inspection stays picked (re-highlighted). The ghost
   * is gone after a success (view back at the origin) and stays at the restored pose after a rollback.
   */
  private finishPicks(inspection: GaugingElement): void {
    this.clearTarget();
    if (this.inspection === inspection) {
      this.restoreMaterial(inspection);
      this.paintInspection(inspection);
    }
    this.syncOriginGhost();
  }

  /**
   * Ghost of the picked inspection's original spot while the view shows a region this tool chose and
   * the inspection's own mesh is hidden; removed otherwise. Call only while the real mesh holds its
   * original pose (an overlay preview never moves it; a mesh preview only exists while it is visible).
   */
  private syncOriginGhost(): void {
    const inspection = this.inspection;
    if (inspection?.SurfaceDesign && this.regionSwitch.isShowingToolRegion() && !this.isInspectionShown(inspection)) {
      // an instanced point: its batch's shape geometry at the slot's (saved) world matrix
      this.originGhost.show(inspection);
    } else {
      this.originGhost.remove();
    }
    this.requestRender();
  }

  private clearTarget(): void {
    if (this.target) this.restoreMaterial(this.target);
    this.preview.remove();
    this.targetHighlight.remove();
    this.target = null;
    this.targetNormal = null;
    this.requestRender();
  }

  private paintInspection(el: GaugingElement): void {
    const isPoint = retargetMethodOf(el.elementResult?.InspectionMethodId) === 'POINT';
    this.paint(el, isPoint ? GraphicsMaterials.pointMaterialBlue : GraphicsMaterials.blueMaterial);
  }

  /**
   * Saves the exact current material object before painting (never defaultMaterial). An instanced
   * point's material setter recolours its instance by material name, so its exact colour is saved too.
   */
  private paint(el: WorkableElement, material: THREE.Material): void {
    const mesh = el?.SurfaceDesign;
    if (!mesh) return;
    if (!this.savedMaterials.has(el.Guid)) {
      const entry: SavedMaterial = { el, mesh, saved: mesh.material, applied: material };
      const color = new THREE.Color();
      if (!this.isRealMesh(el) && el instanceof GaugingElement && this.pointManager?.getInstanceColor(el, color)) entry.color = color;
      this.savedMaterials.set(el.Guid, entry);
    }
    mesh.material = material;
    this.requestRender();
  }

  private restoreMaterial(el: WorkableElement): void {
    const entry = this.savedMaterials.get(el.Guid);
    if (!entry) return;
    this.restoreEntry(entry);
    this.savedMaterials.delete(el.Guid);
  }

  /** Puts the saved material (and instance colour) back, only if nobody replaced our highlight meanwhile. */
  private restoreEntry(entry: SavedMaterial): void {
    if (entry.mesh.material !== entry.applied) return; // e.g. an async colour refresh repainted it
    entry.mesh.material = entry.saved;
    if (entry.color && entry.el instanceof GaugingElement) this.pointManager?.setInstanceColorDirect(entry.el, entry.color);
    this.requestRender();
  }

  private buildPointMesh(target: WorkableElement, point: THREE.Vector3 | undefined, normal: THREE.Vector3 | null): THREE.Mesh | null {
    const type = pointTypeForTarget(target);
    if (!type || !point || !normal) return null;
    const scope = scopeForTarget(target, this.inspection?.elementResult?.Scope);
    try {
      const built = this.graphicsRenderDecal.createPlatePointFromPosition(
        [point.x, point.y, point.z], target, type, scope, normal.clone(), target.section?.box3);
      return (built?.point as THREE.Mesh) ?? null;
    } catch (error) {
      console.warn('[InspectionRetarget] cannot build the inspection point', error);
      return null;
    }
  }

  /** World normal of the picked face, flipped to face the viewer. */
  private normalFor(target: WorkableElement, hit: THREE.Intersection): THREE.Vector3 {
    // worldNormalFromIntersection returns null when the hit has no face (lines/points): fall back to
    // the target's element normal, then to its section normal.
    const normal = worldNormalFromIntersection(hit)
      ?? target.elementNormal?.clone() ?? target.section?.sectionNormal?.clone() ?? new THREE.Vector3(0, 0, 1);
    if (normal.lengthSq() > 0) normal.normalize();
    if (this.lastRayDirection && normal.dot(this.lastRayDirection) > 0) normal.negate();
    return normal;
  }

  private validateOptions(): RetargetValidateOptions {
    const chosen = this.regionSwitch.chosenRegionId;
    return { extraRegionIds: chosen ? [chosen] : [] };
  }

  /** True only when the structure region is known and its properties are explicitly not loaded yet. */
  private isRegionLoading(regionId: string | undefined): boolean {
    if (!regionId) return false;
    return this.assetManager?.asset?.StructureCampaigns?.[0]?.RegionById(regionId)?.isPropertiesLoaded === false;
  }

  /** First visible real mesh: instanced inspection batches and this tool's own preview/ghost meshes are skipped. */
  private firstVisibleMeshHit(hits: THREE.Intersection[]): THREE.Intersection | null {
    return hits.find(h => {
      const o = h.object as THREE.Mesh;
      return o.isMesh && !(o as any).isInstancedMesh && !this.isToolMesh(o) && this.isVisibleChain(o);
    }) ?? null;
  }

  private isToolMesh(o: THREE.Object3D): boolean {
    return o.name === 'RetargetOriginGhost' || o.name === 'RetargetTargetHighlight' || o.userData?.retargetPreview === true;
  }

  /** `obj` and all its ancestors are visible (true for a missing object). */
  private isVisibleChain(obj: THREE.Object3D | null | undefined): boolean {
    for (let o = obj; o; o = o.parent) if (!o.visible) return false;
    return true;
  }

  /** Explicit manager, or the static one dev sets once inspections are loaded (after initialize()). */
  private get pointManager(): InspectionPointInstanceManager | null {
    return this.instanceManager ?? GaugingElement.instanceManager ?? null;
  }

  /** False for an instanced POINT, whose SurfaceDesign is a PhantomSurfaceDesign (no Object3D, no geometry). */
  private isRealMesh(el: WorkableElement | null | undefined): boolean {
    return el?.SurfaceDesign instanceof THREE.Object3D;
  }

  /**
   * Whether the inspection is on screen. A real mesh: its own group chain. An instanced point: its
   * instance visibility and its batch's group chain (structure section and region groups).
   */
  private isInspectionShown(el: GaugingElement): boolean {
    const design = el?.SurfaceDesign;
    if (!design) return false;
    if (this.isRealMesh(el)) return this.isVisibleChain(design);
    const manager = this.pointManager;
    if (!manager) return design.visible !== false;
    return manager.getInstanceVisible(el) && this.isVisibleChain(manager.getInstanceInfo(el)?.mesh ?? el.section?.group);
  }

  /**
   * Nearest shown instanced POINT on the ray. The raycast ignores visibility, so the instance state and
   * the batch's section/region groups are checked here (dev's selection only checks the instance).
   */
  private instancedInspectionHit(raycaster: THREE.Raycaster): InspectionHit | null {
    const manager = this.pointManager;
    const targets = manager?.getRaycastTargets() ?? [];
    if (!targets.length) return null;
    for (const hit of raycaster.intersectObjects(targets, false)) {
      if (hit.instanceId === undefined) continue;
      const el = manager.resolveInstance(hit.object as THREE.InstancedMesh, hit.instanceId);
      if (el && manager.getInstanceVisible(el) && this.isVisibleChain(hit.object)) return { el, distance: hit.distance };
    }
    return null;
  }

  /** dev renders on demand: every visible change of this tool asks for a frame. */
  private requestRender(): void {
    this.graphics?.requestRender?.();
  }

  /**
   * Element hit in the given collections. Mesh identity first (detailed meshes, SECTIONS and old
   * format); then the element id from the hit (merged lightsection mesh -> elementMap, lightsection
   * overlay -> userData.elementId, detailed mesh -> userData.Guid / name) looked up in its section,
   * preferring the section whose group holds the hit mesh. Ids match with or without dashes.
   */
  private findElement(collections: Collection[], hit: THREE.Intersection): WorkableElement | null {
    const obj = hit?.object;
    if (!obj) return null;
    const sections: Section[] = (collections ?? []).flatMap(c => c?.regionsWorkableElements ?? []).flatMap(r => r?.sections ?? []);
    for (const section of sections) {
      for (const el of section.surfaceElements) if (el.SurfaceDesign === obj) return el;
      for (const el of section.linearElements) if (el.SurfaceDesign === obj) return el;
    }
    const ref = elementRefFromHit(hit, sections.map(s => s.Guid));
    if (!ref?.sectionId) return null;
    const matching = sections.filter(s => sameGuid(s.Guid, ref.sectionId));
    const holdsHit = (s: Section) => { for (let o = obj.parent; o; o = o.parent) if (o === s.group) return true; return false; };
    for (const section of [...matching.filter(holdsHit), ...matching.filter(s => !holdsHit(s))]) {
      for (const id of guidVariants(ref.elementId)) {
        const el = section.getElementById(id);
        if (el) return el;
      }
    }
    return null;
  }

  private isSectionVisible(target: WorkableElement): boolean {
    try {
      return target.section ? target.section.visible : true;
    } catch {
      return true; // detached group chain (no parent): treat as visible
    }
  }

  private notify(message: string): void {
    this.snackBar?.open(message, '', { horizontalPosition: 'center', verticalPosition: 'bottom', duration: 3000 });
  }

  //#endregion
}
